import {classes, property_of} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {countries, country_codes_lookup} from '@bitstillery/common/lib/countries'
import {random_string} from '@bitstillery/common/lib/utils'
import {content_props, tip} from '@bitstillery/common/components/ui/tip/tip'
import m from '@bitstillery/mithril'
import type {Instance as TippyInstance, Props as TippyProps} from 'tippy.js'

export interface CountryAttrs {
    country_code?: string | null
    disabled?: boolean
    language?: string
    size?: 's' | 'xs' | 'd' | 'l' | 'xl'
    className?: string
    onclick?: () => unknown
    tip?: string | (() => string)
    tip_config?: Partial<TippyProps>
    type?: 'only_flag' | 'flag_with_country'
}

export class Country extends MithrilComponent<CountryAttrs> {
    tippy?: TippyInstance[]
    id = random_string(8, false)

    override oncreate(vnode: m.Vnode<CountryAttrs>) {
        const tip_config: Partial<TippyProps> = {
            allowHTML: true,
            animation: 'scale',
            appendTo: document.body,
            ...content_props(this.update_tip(vnode)),
            delay: 500,
            inertia: true,
            interactive: true,
            onTrigger: (instance: TippyInstance) => {
                instance.setProps(content_props(this.update_tip(vnode)))
            },
            touch: 'hold',
            zIndex: 10_000_000_000_000_000,
        }

        if (vnode.attrs.tip_config) {
            Object.assign(tip_config, vnode.attrs.tip_config)
        }
        if (vnode.attrs.tip) {
            this.tippy = tip(`#${this.id}`, tip_config)
        }
    }

    update_tip(vn: m.Vnode<CountryAttrs>) {
        if (typeof vn.attrs.tip === 'function') {
            return vn.attrs.tip()
        }
        return vn.attrs.tip
    }

    view(vnode: m.Vnode<CountryAttrs>) {
        let country_code = 'un'

        // Normalize to string - handles Signal (.value), string, number, null/undefined
        const toStr = (v: unknown): string => {
            if (typeof v === 'string') return v
            const signal_value = property_of(v, 'value')
            if (typeof signal_value === 'string') return signal_value
            if (v != null && (typeof v === 'number' || typeof v === 'boolean')) return String(v)
            return ''
        }

        const langStr = toStr(vnode.attrs.language)
        const codeStr = toStr(vnode.attrs.country_code)

        if (langStr) {
            country_code = langStr.toLowerCase() || 'un'
            if (['en', 'en-gb'].includes(country_code)) {
                country_code = 'gb'
            } else if (country_code === 'ar') {
                // Syria
                country_code = 'sy'
            } else if (country_code === 'zh') {
                country_code = 'cn'
            } else if (country_code === 'ja') {
                country_code = 'jp'
            }
        } else if (codeStr) {
            country_code = codeStr.toLowerCase() || 'un'
        }
        if (country_code.length > 2) {
            country_code = country_codes_lookup[country_code.toLowerCase()] || country_code
        }

        const country_name = countries[country_code.toUpperCase()] || 'Country not known'
        const country_text = country_name ? `${country_name} (${country_code})` : codeStr || 'unknown'
        const size = vnode.attrs.size || 's'

        return (
            <div
                onclick={vnode.attrs.onclick}
                title={country_name}
                className={classes('c-country', vnode.attrs.className, vnode.attrs.disabled && 'disabled')}
                id={this.id}
            >
                <div className={classes('fi', `fi-${country_code}`, `icon-${size}`)} />
                {vnode.attrs.type === 'flag_with_country' && <span>{country_text}</span>}
                {vnode.children}
            </div>
        )
    }
}
